import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { BuyerLegalType } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { useSaveOrganization } from "../useOrganizationEditor";

interface FormValues {
  buyerLegalType: BuyerLegalType | "";
}

interface Props {
  currentValue: BuyerLegalType;
  choices: BuyerLegalType[];
}

export const ChangeBuyerLegalType = ({ currentValue, choices }: Props) => {
  const { t } = useTranslation();
  const onlySupportChangesIt = t("Only SourceMagnet support can change it afterwards. Ask through «Help» in the menu.");

  if (choices.length === 0) {
    return (
      <FormControl id="buyerLegalType" mt="20px">
        <FormLabel as="div">{t("Buyer legal type")}</FormLabel>
        <Text py="2">{t(currentValue)}</Text>
        <FormHelperText>{onlySupportChangesIt}</FormHelperText>
      </FormControl>
    );
  }
  return <NarrowBuyerLegalType currentValue={currentValue} choices={choices} onlySupportChangesIt={onlySupportChangesIt} />;
};

const NarrowBuyerLegalType = ({
  currentValue,
  choices,
  onlySupportChangesIt,
}: Props & { onlySupportChangesIt: string }) => {
  const { saveOrganization, isSaving } = useSaveOrganization();
  const toast = useToast();
  const { t } = useTranslation();
  const displayer = useApiError();
  const confirmation = useConfirmationModal();
  const { register, handleSubmit, watch } = useForm<FormValues>({ defaultValues: { buyerLegalType: "" } });

  const selected = watch("buyerLegalType");

  const save = async (buyerLegalType: BuyerLegalType) => {
    try {
      await saveOrganization({ buyerLegalType: { value: buyerLegalType } });
      toast({ title: t("Buyer legal type has been updated!"), status: "success" });
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const onSubmit = (values: FormValues) => {
    const chosen = values.buyerLegalType;
    if (!chosen) return;
    confirmation.show({
      title: t("Save «{{legalType}}» as your buyer legal type?", { legalType: t(chosen) }),
      description: t(
        "Every notice your organisation sends to Doffin will state it, including those for procurements already published. {{onlySupport}}",
        { onlySupport: onlySupportChangesIt },
      ),
      confirmButtonText: t("Save change") ?? undefined,
      onConfirmed: () => save(chosen),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl id="buyerLegalType" mt="20px">
        <FormLabel>{t("Buyer legal type")}</FormLabel>
        <Alert status="warning" rounded="md" mb="2">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {t(
              "Doffin no longer accepts «{{legalType}}» without saying who controls the organisation. Choose the one that fits, so your notices can be published.",
              { legalType: t(currentValue) },
            )}
          </AlertDescription>
        </Alert>
        <Select placeholder={t("Choose who controls the organisation") ?? ""} {...register("buyerLegalType")}>
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {t(choice)}
            </option>
          ))}
        </Select>
      </FormControl>
      {selected && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant="solid"
          colorScheme="teal"
          size="sm"
          type="submit"
          isLoading={isSaving}
        >
          {t("Save change")}
        </Button>
      )}
      {confirmation.modal}
    </form>
  );
};
